// Device dates arrive in three shapes: "YYYY-MM-DD" from the picker, an Excel
// serial (45483) from the bulk upload's raw sheet read, and "8/7/24" from CSV
// imports. Feeding a serial to a date picker renders "01/01/45483". This is
// the same normaliser the backend applies on write (device-date.ts), so the
// drawer shows a real date for legacy rows and the upload sends ISO strings.

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/
const EXCEL_SERIAL = /^\d{5}$/

// Excel counts days from 1899-12-30 (the 1900 leap-year bug is baked in).
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30)
const DAY_MS = 24 * 60 * 60 * 1000
const EXCEL_SERIAL_MIN = 20000 // 1954-10-03
const EXCEL_SERIAL_MAX = 80000 // 2119-01-19

const pad = (n) => String(n).padStart(2, '0')

function isoFromUtc(date) {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate(),
  )}`
}

function isoFromParts(year, month, day) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const probe = new Date(Date.UTC(year, month - 1, day))
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) {
    return null
  }
  return isoFromUtc(probe)
}

export function excelSerialToIso(serial) {
  if (
    !Number.isFinite(serial) ||
    serial < EXCEL_SERIAL_MIN ||
    serial > EXCEL_SERIAL_MAX
  ) {
    return null
  }
  return isoFromUtc(new Date(EXCEL_EPOCH_MS + Math.floor(serial) * DAY_MS))
}

// "YYYY-MM-DD" for every shape we have seen, '' for empty, and the trimmed
// input unchanged when it is not recognised (nothing is silently dropped).
export function normalizeDateInput(value) {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : isoFromUtc(value)
  }
  if (typeof value === 'number') {
    return excelSerialToIso(value) || String(value)
  }
  const raw = String(value).trim()
  if (raw === '') return ''

  const iso = raw.match(ISO_DATE)
  if (iso) {
    return isoFromParts(Number(iso[1]), Number(iso[2]), Number(iso[3])) || raw
  }
  if (EXCEL_SERIAL.test(raw)) {
    return excelSerialToIso(Number(raw)) || raw
  }
  const us = raw.match(US_DATE)
  if (us) {
    let year = Number(us[3])
    // Same rule as NfDatePicker: 00-49 is 20xx, 50-99 is 19xx.
    if (us[3].length === 2) year += year < 50 ? 2000 : 1900
    return isoFromParts(year, Number(us[1]), Number(us[2])) || raw
  }
  return raw
}
